import type { components } from "@contracts/generated/editor-service";

/**
 * Is this destination reference a NAME for a destination, or the destination?
 *
 * A MIRROR OF `editor_service/alert_destinations.py`, not a second authority.
 * The server runs the same rules before any write and its
 * `422 alert_destination_refused` is what decides; this exists so the refusal
 * arrives before the request rather than after it — which for THIS field is not
 * only a better message. A refusal that arrives from the server has already put
 * a pasted address, phone number or token in a request body, in the browser's
 * network panel, and in whatever proxies sit between. Refusing locally means
 * the value never leaves the input it was typed into.
 *
 * `AlertMatrixEditor`'s slug check runs first and catches everything with
 * punctuation in it (`oncall@example.org`, `https://…/hook`). These rules are
 * about what survives the flattening — `oncall_example_com`, `tel_15551234567`,
 * an issuer-prefixed token — every one of which is a legal slug and none of
 * which is a name.
 *
 * A `null` result is only permission to send the name to the service. The
 * fetched destination registry and the server write gate decide whether the
 * reference is known, active, and authorized; this client mirror never does.
 */

/**
 * The server's own refusal vocabulary, taken from the generated contract so a
 * member the service adds or renames breaks the typecheck here rather than
 * drifting into a second, quieter vocabulary.
 */
export type DestinationRefusalCode =
  components["schemas"]["DestinationRefusalCode"];

/** `AlertDestinationRefusal`, the typed `details` payload of the 422. */
export type AlertDestinationRefusal =
  components["schemas"]["AlertDestinationRefusal"];

const SEPARATORS = /[^a-z0-9]+/;

/** Mirrors `_URL_SCHEMES`. Matched only as the first label. */
const URL_SCHEMES = new Set([
  "http",
  "https",
  "ftp",
  "ftps",
  "sftp",
  "smtp",
  "smtps",
  "imap",
  "imaps",
  "pop3",
  "mailto",
  "sip",
  "sips",
  "ldap",
  "ldaps",
  "webcal",
  "rtsp",
  "tftp",
]);

/** Mirrors `_GENERIC_DOMAINS`. Refused as a last label. */
const GENERIC_DOMAINS = new Set([
  "com",
  "org",
  "net",
  "gov",
  "edu",
  "mil",
  "int",
  "biz",
  "coop",
  "aero",
  "museum",
]);

/** Mirrors `_SECOND_LEVEL_DOMAINS`. */
const SECOND_LEVEL_DOMAINS = new Set([
  "co",
  "com",
  "org",
  "net",
  "gov",
  "edu",
  "ac",
  "mil",
  "sch",
  "muni",
]);

/** Mirrors `_COUNTRY_DOMAINS`. Refused only as a `<second-level>_<country>` tail. */
const COUNTRY_DOMAINS = new Set([
  "il",
  "uk",
  "au",
  "br",
  "nz",
  "za",
  "in",
  "jp",
  "kr",
  "mx",
  "id",
  "tr",
  "ve",
  "th",
  "cn",
  "ru",
  "de",
  "fr",
  "es",
  "it",
  "nl",
  "se",
  "dk",
  "fi",
  "no",
  "pl",
  "pt",
  "ro",
  "gr",
  "hu",
  "cz",
  "ch",
  "at",
  "be",
  "ie",
  "ca",
  "us",
  "ar",
  "cl",
  "ph",
  "my",
  "sg",
  "hk",
  "tw",
  "ua",
]);

/** Mirrors `_TOKEN_PREFIXES`. */
const TOKEN_PREFIXES = [
  "ghp_",
  "gho_",
  "ghu_",
  "ghs_",
  "ghr_",
  "github_pat_",
  "glpat_",
  "xoxb_",
  "xoxp_",
  "xoxa_",
  "xoxs_",
  "xoxr_",
  "sk_live_",
  "sk_test_",
  "pk_live_",
  "pk_test_",
  "rk_live_",
  "rk_test_",
  "npm_",
  "dckr_pat_",
  "shpat_",
  "shpss_",
  "sq0atp_",
  "sq0csp_",
  "ya29_",
  "amzn_mws_",
  "akia",
  "asia",
  "aroa",
  "aida",
  "agpa",
  "anpa",
  "anva",
  "aipa",
  "abia",
  "acca",
];

/**
 * Mirrors `_SECRET_NOUNS` / `_SECRET_QUALIFIERS` / `_QUALIFIED_NOUNS`, and is
 * COMPOSED for the same two reasons the service composes it: a secret word is a
 * noun with an optional qualifier, and writing the products out by hand would
 * put a client secret, a private key and a refresh token into a bundle input as
 * literals — which the web bundle credential invariants (`apps/editor-web/src/invariants/`) forbids outright.
 *
 * `key` and `string` never stand alone: `key_accounts` is a destination name.
 */
const SECRET_NOUNS = [
  "secret",
  "secrets",
  "token",
  "tokens",
  "password",
  "passwords",
  "passwd",
  "pwd",
  "credential",
  "credentials",
  "bearer",
];
const SECRET_QUALIFIERS = [
  "api",
  "access",
  "private",
  "public",
  "signing",
  "shared",
  "client",
  "auth",
  "bearer",
  "refresh",
  "webhook",
  "connection",
  "sas",
  "encryption",
];
const QUALIFIED_NOUNS = ["key", "keys", "token", "secret", "string"];

/** Whole labels and adjacent pairs; never `api` or `key` on its own. */
const SECRET_WORDS = new Set([
  ...SECRET_NOUNS,
  ...SECRET_QUALIFIERS.flatMap((qualifier) =>
    QUALIFIED_NOUNS.map((noun) => `${qualifier}${noun}`),
  ),
]);

/** Mirrors `_MIN_TOKEN_BODY`, `_MIN_OPAQUE_LABEL`, `_MIN_HEX_LABEL`. */
const MIN_TOKEN_BODY = 16;
const MIN_OPAQUE_LABEL = 20;
const MIN_HEX_LABEL = 16;
/** Mirrors `_MIN_NUMERIC_LABELS` / `_MIN_PHONE_DIGITS`. */
const MIN_NUMERIC_LABELS = 3;
const MIN_PHONE_DIGITS = 7;
/** Mirrors `_DIGIT_RUN`. */
const DIGIT_RUN = /\d{7,}/;

function labelsOf(ref: string): string[] {
  return ref.toLowerCase().split(SEPARATORS).filter(Boolean);
}

function looksLikeAHost(labels: readonly string[]): boolean {
  if (labels.length < 2) {
    return false;
  }
  if (GENERIC_DOMAINS.has(labels[labels.length - 1] as string)) {
    return true;
  }
  if (
    labels.length >= 3 &&
    COUNTRY_DOMAINS.has(labels[labels.length - 1] as string) &&
    SECOND_LEVEL_DOMAINS.has(labels[labels.length - 2] as string)
  ) {
    return true;
  }
  return labels.includes("at") && labels.includes("dot");
}

function carriesCredentialMaterial(
  ref: string,
  labels: readonly string[],
): boolean {
  const lowered = ref.toLowerCase();
  for (const prefix of TOKEN_PREFIXES) {
    if (lowered.startsWith(prefix)) {
      const body = lowered.slice(prefix.length).replace(/[^a-z0-9]/g, "");
      if (body.length >= MIN_TOKEN_BODY) {
        return true;
      }
    }
  }
  if (labels.some((label) => SECRET_WORDS.has(label))) {
    return true;
  }
  return labels.some(
    (label, index) =>
      index > 0 && SECRET_WORDS.has(`${labels[index - 1] as string}${label}`),
  );
}

function looksGenerated(labels: readonly string[]): boolean {
  return labels.some((label) => {
    const hasDigit = /\d/.test(label);
    if (label.length >= MIN_OPAQUE_LABEL && hasDigit && /[a-z]/.test(label)) {
      return true;
    }
    return (
      label.length >= MIN_HEX_LABEL && hasDigit && /^[0-9a-f]+$/.test(label)
    );
  });
}

function looksLikeAPhoneNumber(
  ref: string,
  labels: readonly string[],
): boolean {
  if (DIGIT_RUN.test(ref)) {
    return true;
  }
  let numeric: string[] = [];
  for (const label of labels) {
    if (/^\d+$/.test(label)) {
      numeric = [...numeric, label];
      const digits = numeric.reduce((total, part) => total + part.length, 0);
      const year = Number(numeric[0]);
      const month = Number(numeric[1]);
      const day = Number(numeric[2]);
      const dateLike =
        numeric.length === 3 &&
        numeric[0]?.length === 4 &&
        year >= 1900 &&
        year <= 2199 &&
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= 31;
      if (
        numeric.length >= MIN_NUMERIC_LABELS &&
        digits >= MIN_PHONE_DIGITS &&
        !dateLike
      ) {
        return true;
      }
    } else {
      numeric = [];
    }
  }
  return false;
}

/**
 * The reason `ref` is not a destination reference, or `null` if it is one.
 *
 * Precedence mirrors the server's, most-specific form first: a scheme, then a
 * host, then a known issuer prefix, then a generated-looking label, and a bare
 * digit run last. A value can satisfy several rules and only one reason is
 * rendered, so the order is part of the contract, not an implementation detail.
 */
export function classifyDestinationRef(
  ref: string,
): DestinationRefusalCode | null {
  const labels = labelsOf(ref);
  if (labels.length === 0) {
    return null;
  }
  if (URL_SCHEMES.has(labels[0] as string)) {
    return "inline_endpoint_url";
  }
  if (looksLikeAHost(labels)) {
    return "inline_endpoint_host";
  }
  if (carriesCredentialMaterial(ref, labels)) {
    return "credential_material";
  }
  if (looksGenerated(labels)) {
    return "opaque_secret_material";
  }
  if (looksLikeAPhoneNumber(ref, labels)) {
    return "inline_phone_number";
  }
  return null;
}

/**
 * What to tell the clinician, per reason.
 *
 * Never quotes the entry — the message is rendered next to the input that still
 * holds it, so repeating it adds nothing and would put it in every screenshot
 * of a failed save.
 */
export const DESTINATION_REFUSAL_MESSAGES: Record<
  DestinationRefusalCode,
  string
> = {
  inline_endpoint_url:
    "This looks like a URL, not a destination reference. Enter the name the webhook was configured under; the URL itself stays outside clinical content.",
  inline_endpoint_host:
    "This looks like an email address or a hostname, not a destination reference. Enter the name that destination was configured under; the address itself stays outside clinical content.",
  inline_phone_number:
    "This looks like a phone number, not a destination reference. Enter the name that destination was configured under; the number itself stays outside clinical content.",
  credential_material:
    "This looks like a credential. A destination reference names a destination; tokens, keys and secrets never belong in clinical content — and a published version cannot be edited to remove one.",
  opaque_secret_material:
    "This looks like a generated value rather than a name. A destination reference is something a person chose and can recognise later.",
  unknown_reference:
    "This reference is not present in the current destination authority. Choose an approved destination from the registry.",
  revoked_reference:
    "This destination reference has been revoked. Choose an active destination; existing pinned versions remain removable.",
  environment_scope_mismatch:
    "This destination is not authorized in this environment. Choose one offered by the current registry.",
  project_scope_mismatch:
    "This destination is not authorized for this project's preview target or explicit project scope.",
};
